/**
 * ProfilePictureViewer
 * Component that wraps an avatar in a dialog to allow viewing the full-size profile picture
 */

import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from '@/shared/components/ui/dialog';
import { useState } from 'react';
import { ZoomIn, Loader2 } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface ProfilePictureViewerProps {
  imageUrl?: string;
  altText: string;
  children: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function ProfilePictureViewer({ imageUrl, altText, children }: ProfilePictureViewerProps) {
  const [open, setOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // If no image URL, just render children without dialog functionality
  if (!imageUrl) {
    return <>{children}</>;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setImageLoaded(false); // Reset on close
      }}
    >
      <DialogTrigger asChild>
        <button
          className='relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full'
          aria-label={`Ver foto de perfil de ${altText}`}
        >
          {children}
          {/* Hover overlay with zoom icon */}
          <div className='absolute inset-0 bg-black/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
            <ZoomIn className='h-6 w-6 text-white' />
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className='max-w-sm p-2 overflow-hidden bg-card border border-border shadow-2xl rounded-xl [&>button]:hidden'>
        {/* Accessibility: visually hidden title */}
        <DialogTitle className='sr-only'>Foto de perfil de {altText}</DialogTitle>
        <DialogDescription className='sr-only'>Vista ampliada de la foto de perfil</DialogDescription>

        {/* Loading state */}
        {!imageLoaded && (
          <div className='w-full h-64 flex items-center justify-center'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        )}

        <img
          src={imageUrl}
          alt={altText}
          className={`w-full h-auto object-contain max-h-[40vh] rounded-lg ${!imageLoaded ? 'hidden' : ''}`}
          onLoad={() => setImageLoaded(true)}
        />
      </DialogContent>
    </Dialog>
  );
}
